import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { ActionModal, Table } from '@skyline-platform/alchemy';
import { SearchBarComponent } from '@components/SearchBar/SearchBarComponent';
import { SkipToMainContent } from '@components/Misc';
import { getAppliances } from '@utils/graphql/creators/appliances';
import { useEffectOnce } from '@utils/react/hooks';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { parseSSoTEvent, SSOT_ACTIONS } from '@utils/eventHandler';
import { ApplianceActions } from './ApplianceActions';
import {
  buildSearchCriteria,
  normalizeStringFields,
} from '../utils/helpers';
import '../split/page.css';
import { getLogger } from '@utils/general';
import PropTypes from 'prop-types';
import { useServerPagination } from '@utils/react/hooks/useServerPagination';
import {
  patchRow, removeRow, addRow,
} from '../split/utils/helpers';

const logger = getLogger('src.pages.appliances.AllAppliances');

const APPLIANCE_FETCH_FIELDS = [
  'id',
  'name',
  'management_address',
  'public_address',
  'location',
  'hostname',
  'model',
  'version',
  'offline_source.id',
  'offline_source.name',
  'offline_source.description',
  'offline_source.sdp_input',
  'offline_source.port',
  'offline_source.path',
  'offline_source.scheme',
  'offline_source.appliance.id',
  'offline_source.appliance.name',
  'offline_source.appliance.management_address',
  'offline_source.device.id',
  'offline_source.device.name',
  'offline_source.device.network_address',
];

const SEARCH_FIELD_MAP = {
  name: 'name',
  hostname: 'hostname',
  location: 'location',
  management_address: 'management_address',
  public_address: 'public_address',
  model: 'model',
  version: 'version',
  offlineSource: 'offline_source_name',
};

// Fields rendered directly in the Appliances table (non-nested).
// Events where none of these change (e.g. connection_count-only updates) are dropped.
const APPLIANCE_PATCH_FIELDS = new Set([
  'name',
  'hostname',
  'location',
  'management_address',
  'public_address',
  'model',
  'version',
]);

export const AllAppliances = ({ userIsAdmin }) => {
  const searchOptions = [
    { key: 'Appliance Name', value: 'name' },
    { key: 'Hostname', value: 'hostname' },
    { key: 'Location', value: 'location' },
    { key: 'Management Address', value: 'management_address' },
    { key: 'Public Address', value: 'public_address' },
    { key: 'Model', value: 'model' },
    { key: 'Software Version', value: 'version' },
    { key: 'Offline Profile', value: 'offlineSource' },
  ];
  const [selectedFields, setSelectedFields] = useState(searchOptions.map((option) => option.value));

  const mapTableData = useCallback((appliances) => {
    return appliances.map((appliance) => {
      const applianceNormalized = normalizeStringFields(appliance, [
        'location',
        'public_address',
        'model',
        'version',
      ]);
      return {
        offlineSource: appliance?.offline_source?.name ?? '',
        ...applianceNormalized,
        action: { ...appliance },
      };
    });
  }, []);

  // Always include source-related options needed for offline_source data
  const fetchAppliances = useCallback((id, criteria, options) => {
    return getAppliances(id, criteria, {
      ...options, includeDeviceQuery: true, includeApplianceQuery: true,
    });
  }, []);

  const {
    rows,
    totalRows,
    loading,
    currentPage,
    rowsPerPage,
    handleSort,
    sortDirection,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    clearSearch,
    setRows,
    setTotalRows,
  } = useServerPagination({
    fetchFn: fetchAppliances,
    mapFn: mapTableData,
    fetchFields: APPLIANCE_FETCH_FIELDS,
    defaultSort: { field: 'name', direction: 'asc' },
    // SORT_FIELD_MAP is not needed for this file because all table columns match the backend field names
    // it will need to be added in the future if there is a column name that does not match the backend
    // field name (e.g. offlineSource)
    // TODO: public_address is not in the backend's _sortable_fields
    // check with the stream team before adding that to sortFieldMap here
    sortFieldMap: { offlineSource: 'offline_source_name' },
    enableSort: true,
    onError: (error) => logger.error(error),
  });

  const hasActiveCriteriaRef = useRef(false);

  const handleServerSearch = useCallback((searchTerm, fields) => {
    if (!searchTerm) {
      if (hasActiveCriteriaRef.current) {
        hasActiveCriteriaRef.current = false;
        clearSearch();
      }
      return;
    }
    const criteria = buildSearchCriteria(searchTerm, fields, SEARCH_FIELD_MAP);
    if (Object.keys(criteria).length === 0) return;
    hasActiveCriteriaRef.current = true;
    handleSearch(criteria);
  }, [handleSearch, clearSearch]);

  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const tableContext = {
    rowsRef,
    setRows,
    // removeRow calls setSelectedRowIds/setSelectedId to clean up selection state when a
    // row is deleted. AllAppliances has no row selection, so these are no-ops that satisfy
    // the tableContext interface without wiring up state that isn't used.
    setSelectedRowIds: () => { },
    setSelectedId: () => { },
  };

  /***** SUBSCRIPTIONS *****/
  // AllAppliances uses inline patchRow/removeRow/addRow rather than handleTableEvent because
  // Appliance events only ever affect Appliance rows — there is no fan-out to other model types.
  // handleTableEvent is the right tool when a single event (e.g. an appliance rename) needs to
  // ripple into many rows of a different model (e.g. streams). That complexity is not needed
  // here, so the simpler inline approach is used instead.
  //
  // APPLIANCE_PATCH_FIELDS gates Update events so that high-frequency fields not shown
  // in this table (e.g. connection_count) produce an empty rowPatch and are dropped immediately.
  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    const handleEvent = async (eventMessage) => {
      // Handle Source update events for offline source name changes
      const parsedSource = parseSSoTEvent(eventMessage, 'Source');
      if (parsedSource) {
        const { action, data } = parsedSource;
        if (action === SSOT_ACTIONS.UPDATE && data.current?.category === 'Offline') {
          const { current } = data;
          const sourceId = current.id;
          if (!sourceId) return;
          // Find the row in the table that has this offline source
          const matchingRow = rowsRef.current.find(
            (row) => row.offline_source?.id === sourceId
          );
          if (!matchingRow) return;
          // Fetch the full offline source from the API so all changed fields
          // (name, description, sdp_input, port, path, scheme, etc.) are
          // updated in the row — not just the name.
          try {
            const { query, execute } = getAppliances(matchingRow.id, null, {
              includeDeviceQuery: true,
              includeApplianceQuery: true,
            });
            const offlineSourceFields = APPLIANCE_FETCH_FIELDS.filter(
              (f) => f === 'id' || f.startsWith('offline_source')
            );
            query.addReturnProperties(...offlineSourceFields);
            const { data: fetched } = await execute();
            const newOfflineSource = fetched?.offline_source ?? null;
            const rowPatch = {
              offlineSource: newOfflineSource?.name ?? '',
              offline_source: newOfflineSource,
            };
            patchRow(matchingRow.id, rowPatch, tableContext, rowPatch);
          } catch (err) {
            logger.error(err);
          }
        }
        return;
      }

      const parsed = parseSSoTEvent(eventMessage, 'Appliance');
      if (!parsed) return;
      const { action, data } = parsed;

      if (action === SSOT_ACTIONS.REMOVE) {
        if (data.previous?.id) {
          removeRow(data.previous.id, tableContext);
          setTotalRows((prev) => Math.max(0, prev - 1));
        }
        return;
      }

      if (!data.current?.id) return;

      if (action === SSOT_ACTIONS.CREATE) {
        const { query, execute } = getAppliances(data.current.id, null, {
          includeDeviceQuery: true,
          includeApplianceQuery: true,
        });
        query.addReturnProperties(...APPLIANCE_FETCH_FIELDS);
        try {
          const { data: applianceData } = await execute();
          if (applianceData) {
            addRow(applianceData, mapTableData, tableContext);
            setTotalRows((prev) => prev + 1);
          }
        } catch (err) {
          logger.error(err);
        }
        return;
      }

      if (action === SSOT_ACTIONS.UPDATE) {
        const { current, previous } = data;
        const rowPatch = {};
        for (const field of APPLIANCE_PATCH_FIELDS) {
          if (current[field] !== undefined && current[field] !== previous?.[field]) {
            rowPatch[field] = current[field] ?? '';
          }
        }
        // Check both sides: SSOT may omit offline_source_id from current when clearing
        // it to null (rather than sending explicit null). Checking previous catches that
        // case — if previous has the field but current doesn't, the source was cleared.
        const sourceIdInEvent = 'offline_source_id' in current || 'offline_source_id' in (previous ?? {});
        const offlineSourceChanged = sourceIdInEvent
          && (current.offline_source_id ?? null) !== (previous?.offline_source_id ?? null);
        if (Object.keys(rowPatch).length === 0 && !offlineSourceChanged) return;

        if (!offlineSourceChanged) {
          patchRow(current.id, rowPatch, tableContext);
          return;
        }

        // offline_source changed — fetch the new source name
        try {
          const { query, execute } = getAppliances(current.id, null, {
            includeDeviceQuery: true,
            includeApplianceQuery: true,
          });
          const offlineSourceFields = APPLIANCE_FETCH_FIELDS.filter(
            (f) => f === 'id' || f.startsWith('offline_source')
          );
          query.addReturnProperties(...offlineSourceFields);
          const { data: fetched } = await execute();
          const newOfflineSource = fetched?.offline_source ?? null;
          // rowPatch updates both the display field (offlineSource) and the raw nested
          // object (offline_source) so the edit form — which reads row.offline_source
          // directly from the spread appliance fields — also sees the updated value.
          const rowPatchWithSource = {
            ...rowPatch,
            offlineSource: newOfflineSource?.name ?? '',
            offline_source: newOfflineSource,
          };
          patchRow(current.id, rowPatchWithSource, tableContext, rowPatchWithSource);
        } catch (err) {
          logger.error(err);
        }
      }
    };
    messageHandler.subscribe(handleEvent);
    return () => {
      messageHandler.unsubscribe(handleEvent);
    };
  });
  /***** END SUBSCRIPTIONS *****/

  const tableColumns = useMemo(() => {
    const cols = [
      {
        field: 'name',
        headerName: 'Appliance Name',
        type: 'string',
        grow: 0.8,
        sortable: true,
      },
      {
        field: 'hostname',
        headerName: 'Hostname',
        type: 'string',
        grow: 1,
        sortable: true,
      },
      {
        field: 'location',
        headerName: 'Location',
        type: 'string',
        grow: 1,
        sortable: true,
      },
      {
        field: 'management_address',
        headerName: 'Management Address',
        type: 'string',
        grow: 1,
        sortable: true,
      },
      {
        field: 'public_address',
        headerName: 'Public Address',
        type: 'text',
        grow: 1,
        sortable: true,
      },
      {
        field: 'model',
        headerName: 'Model',
        type: 'text',
        width: '130px',
        sortable: true,
      },
      {
        field: 'version',
        headerName: 'Software Version',
        type: 'text',
        grow: 0.8,
        sortable: true,
      },
      {
        field: 'offlineSource',
        headerName: 'Offline Profile',
        type: 'text',
        grow: 1,
        sortable: true,
      },
    ];

    if (userIsAdmin) {
      cols.push(
        {
          field: 'action',
          headerName: '',
          cell: (row) => (
            <ActionModal
              className='appliance-row-actions'
              id={row.name}
              title='Toggle Options'
              content={ApplianceActions({ rowInfo: row, id: row.id })}
            />
          ),
          width: '50px',
        });
    }

    return cols;
  }, [userIsAdmin]);

  return (
    <gar-all-appliances>
      <div className='actions-bar'>
        <SearchBarComponent
          searchOptions={searchOptions}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          fieldName='Appliances'
          onSearch={handleServerSearch}
        />
      </div>
      <SkipToMainContent
        text="Skip to pagination controls"
        targetSelector="alc-pagination-bar"
      />
      <div>
        <Table
          tableName='Appliances'
          columns={tableColumns}
          hideInColumnMenu={['action']}
          rows={rows}
          initialSortField='name'
          defaultSortAsc={true}
          sortServer
          onSort={handleSort}
          paginationServer
          paginationTotalRows={totalRows}
          currentPage={currentPage}
          paginationPerPage={rowsPerPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          progressPending={loading}
          sortDirection={sortDirection}
          style={{ height: rows?.length === 0 ? '400px' : undefined }}
        />
      </div>
    </gar-all-appliances>
  );
};

AllAppliances.propTypes = {
  userIsAdmin: PropTypes.bool.isRequired,
};